// The OAuth login of the MCP door (s10-mcp-a): what the app's /connect page reads and decides. The OAuth endpoints themselves
// speak OAuth's own shapes (RFC 6749, 7591, 7009, 8414, 9728), not these.
import { z } from "zod";
import { pageOf, Uuid } from "./common";
import { Role } from "./identity";

/** The scope of the MCP door, and the one a refresh token comes with. */
export const MCP_SCOPE = "mcp";
export const OAUTH_SCOPES = ["mcp", "offline_access"] as const;
export type OAuthScope = (typeof OAUTH_SCOPES)[number];

/** Where `authorize` sends the person when the client or its redirect cannot be trusted with the answer. */
export const CONNECT_PAGE_ERRORS = ["client_missing", "client_unknown", "client_document_failed", "redirect_uri_missing", "redirect_uri_mismatch", "request_invalid"] as const;
export type ConnectPageError = (typeof CONNECT_PAGE_ERRORS)[number];

/** The id in `/connect?request=<id>`: random, base64url. */
export const ConnectRequestId = z.string().regex(/^[A-Za-z0-9_-]{32,64}$/);

export const ConnectWorkspaceDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** A suspended workspace is shown and cannot be chosen. */
  active: z.boolean(),
  /** The person's own role there. */
  role: Role,
  /** What the assistant would act as: the lower of the person's role and Editor. */
  assistantRole: Role,
});
export type ConnectWorkspaceDto = z.infer<typeof ConnectWorkspaceDto>;

export const ConnectRequestDto = z.object({
  application: z.object({
    /** `cimd`: the host of its client_id URL, which the application cannot choose. `dcr`: the name it gave itself. */
    kind: z.enum(["cimd", "dcr"]),
    name: z.string(),
  }),
  redirect: z.object({
    host: z.string(),
    /** The answer goes to a program on this computer. */
    loopback: z.boolean(),
  }),
  scopes: z.array(z.string()),
  expiresAt: z.string(),
  /** Who is signed in and about to decide, so someone else's browser shows it. */
  person: z.object({ name: z.string(), email: z.string().nullable() }),
  /**
   * s10-mcp-b: the application was given a workspace's own address (`/v1/mcp/<workspace>`), so there is nothing to choose:
   * `workspaces` holds that one workspace, or nothing when the person has no role in it.
   */
  workspaceFixed: z.boolean(),
  workspaces: z.array(ConnectWorkspaceDto),
});
export type ConnectRequestDto = z.infer<typeof ConnectRequestDto>;

export const ConnectDecisionRequest = z.discriminatedUnion("decision", [
  z.object({ decision: z.literal("allow"), tenantId: z.string().uuid() }),
  z.object({ decision: z.literal("deny") }),
]);
export type ConnectDecisionRequest = z.infer<typeof ConnectDecisionRequest>;

/**
 * The request id goes in the query string on the read and in the body on the decision, never in the path: the request log
 * writes every path, and the id is a ten-minute right to say yes, kept hashed for that reason. Any other string is not found.
 */
export const ConnectRequestQuery = z.object({ id: z.string() });
export type ConnectRequestQuery = z.infer<typeof ConnectRequestQuery>;

export const ConnectDecisionBody = z.discriminatedUnion("decision", [
  z.object({ id: z.string(), decision: z.literal("allow"), tenantId: z.string().uuid() }),
  z.object({ id: z.string(), decision: z.literal("deny") }),
]);
export type ConnectDecisionBody = z.infer<typeof ConnectDecisionBody>;

/** Where the app sends the browser next: the application's redirect, with `code` or `error`, `state` and `iss`. */
export const ConnectDecisionDto = z.object({ redirectTo: z.string() });
export type ConnectDecisionDto = z.infer<typeof ConnectDecisionDto>;

/**
 * s10-mcp-b: what a workspace segment of an MCP address may hold, `/v1/mcp/<workspace>`: the workspace's id, or `ref:` and the
 * host's own reference, as `resolveTenantRef` reads them. Nothing that needs encoding, so the address compares as it is written.
 */
export const MCP_WORKSPACE_SEGMENT = /^[A-Za-z0-9._~:-]{1,200}$/;

/** s10-mcp-b: one assistant's connection, as „Asistenți conectați” and the owner's list on „Chei API” show it. */
export const McpConnectionDto = z.object({
  id: Uuid,
  /** Named as on /connect: a CIMD client by its address's host, a DCR one by the name it gave itself, never without its badge. */
  application: z.object({ kind: z.enum(["cimd", "dcr"]), name: z.string() }),
  redirectHost: z.string(),
  workspace: z.object({ id: Uuid, name: z.string() }),
  /** Who allowed it, by the name they had then. */
  person: z.object({ id: Uuid.nullable(), name: z.string() }),
  /** What it acts as today: the lower of the person's role there now and Editor. Null when the person has no role there any more; it then ends at its next use. */
  assistantRole: Role.nullable(),
  /** `workspace` when it was made at the workspace's own address, `shared` at `/v1/mcp`, where the person chose the workspace. */
  address: z.enum(["shared", "workspace"]),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type McpConnectionDto = z.infer<typeof McpConnectionDto>;

/** The person's own live connections, in every workspace they are in. */
export const McpConnectionList = z.object({ items: z.array(McpConnectionDto) });
export type McpConnectionList = z.infer<typeof McpConnectionList>;

/** A workspace's live connections, everyone's, for its owner. */
export const McpConnectionPage = pageOf(McpConnectionDto);
export type McpConnectionPage = z.infer<typeof McpConnectionPage>;
